import { Injectable } from "@angular/core";
import { ContentServiceCommand } from "@fabric-types/svc-content";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { DocumentRef, ignoreNil } from "@obg/ngx.global";
import { RouteAction, RouteSelector, RouteService } from "@obg/ngx.router";
import { combineLatest, merge, of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { AppError } from "../app-error/app-error.model";
import { CoreConfig } from "../config/core-config";
import { OverrideData } from "../document-meta/document-meta.model";
import { DocumentMetaService } from "../document-meta/document-meta.service";
import { MessageScopeType, MessageType } from "../message/message.model";
import { MessageService } from "../message/message.service";
import { CoreState } from "../state";

import { PageAction } from "./page.action";
import { DEFAULT_PAGE_CONTENT_TYPE, PAGE_MESSAGE_CONTEXT } from "./page.const";
import { PageSelector2 } from "./page.selector2";
import { PageService } from "./page.service";

@Injectable()
export class PageEffect {
	getGeneralDocumentKey$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.resolveEnd),
			map(({ payload: { current } }) => current),
			filter(
				(route) =>
					!this.coreConfig.isB2b &&
					route.data &&
					!route.data.hasDynamicGamingPageContent &&
					!(route.data.useDynamicContent && !route.data.shouldOverrideDocumentMeta) &&
					route.data.updatePageMeta !== false
			),
			map((route) => {
				const segment = { providerId: DEFAULT_PAGE_CONTENT_TYPE, key: route.name };
				const parentSegments = this.routeService
					.getParentNames(segment.key)
					.map((parent) => ({ providerId: DEFAULT_PAGE_CONTENT_TYPE, key: parent }));

				return PageAction.setByDocumentKey({ segment, parentSegments });
			})
		)
	);

	getPageByKey$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PageAction.getByKey),
			switchMap(({ payload }) =>
				this.service.getPage(payload).pipe(
					map((pageData) => PageAction.getByKeySuccess(pageData)),
					catchError((response: AppError) => of(PageAction.getByKeyFail(response)))
				)
			)
		)
	);

	setPageByDocumentKey$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PageAction.setByDocumentKey),
			concatLatestFrom(() => this.store.select(RouteSelector.getData)),
			switchMap(([{ payload }, routeData]) =>
				this.service.getPage(payload).pipe(
					map((pageData) => {
						if ((routeData as OverrideData)?.customMetaData ?? false) {
							return {
								...pageData,
								page: {
									...pageData.page,
									metaTitle: undefined,
									metaDescription: undefined,
								},
							};
						}
						return pageData;
					}),
					map((pageData) => PageAction.setCurrent(pageData)),
					catchError((response: AppError) => of(PageAction.getPageContentFail(response)))
				)
			)
		)
	);

	documentSet$ = createEffect(
		() =>
			combineLatest([
				this.store.select(PageSelector2.getCurrent).pipe(ignoreNil()),
				this.store.select(RouteSelector.getQueryParams),
			]).pipe(
				map(([page, queryParams]) => {
					// NOTE: Remove this when this.documentRef.obgEventBus is fixed on SSR - AC-8323
					if (this.documentRef.obgEventBus) {
						this.documentRef.obgEventBus?.dispatch(ContentServiceCommand.SetAllFlagsUrl, {
							urls: this.documentMetaService.generateAlternativeUrls(page, false),
							queryParams,
						});
					}
				})
			),
		{ dispatch: false }
	);

	initMessages$ = createEffect(
		() =>
			merge(
				this.messageService.mapErrorByContext$(
					{
						[PageAction.getPageContentFail.type]: PAGE_MESSAGE_CONTEXT.setCurrent,
					},
					MessageScopeType.Global,
					this.coreConfig.isBot ? MessageType.Fatal : MessageType.Error
				),
				this.messageService.mapClearByContext$({
					[PageAction.setCurrent.type]: PAGE_MESSAGE_CONTEXT.setCurrent,
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private service: PageService,
		private messageService: MessageService,
		private routeService: RouteService,
		private coreConfig: CoreConfig,
		private documentRef: DocumentRef,
		private documentMetaService: DocumentMetaService,
		private store: Store<CoreState>
	) {}
}
